// ============================================================
// LigneGamme — UNE ligne de la gamme opératoire.
// ------------------------------------------------------------
// Extraite de `GammeBloc.jsx` le 7 septembre 2026, quand la gamme est
// passée en DEUX BLOCS (réunion du 1er septembre). Le composant parent
// ne rendait plus un tableau mais deux, et il n'avait plus la place :
// il butait sur son plafond du §4.
//
// L'extraction n'est donc pas cosmétique, et elle rend une chose au
// passage : le plafond de `GammeBloc.jsx` descend d'autant, et le
// cliquet de `web/plafond-lignes.test.js` le rend acquis.
//
// ── CE QUI COMPTE DANS CE FICHIER ──
//
// `index` est l'index de la ligne dans la gamme RÉELLE, pas sa place à
// l'écran. Les blocs trient l'affichage ; l'édition, elle, écrit dans
// le tableau d'origine. Confondre les deux ferait modifier une ligne
// pour une autre, en silence.
// ============================================================

import Button from '../../ui/Button.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import Puces from '../../ui/Puces.jsx';
import { formatMesure } from '../../lib/format.js';
import { titreDeLOperation } from './titreOperation.js';
import {
  CATEGORIES_GAMME, UNITES_PAR_CATEGORIE, LIBELLE_CATEGORIE,
} from '@shared/gamme.js';

/** L'unité en clair — c'est elle qui dit si le temps se multiplie. */
const LIBELLE_UNITE = {
  par_piece: '/pièce',
  par_lot: '/lot',
  par_projet: '/projet',
};

export function LigneGamme({
  op, index, machines = [], machine, ouvert, disabled,
  peutMonter, peutDescendre,
  modifier, changerCategorie, deplacer, onOuvrir, onRetirer,
}) {
  const i = index;
  return (
            <tr key={op.id ?? `n${i}`} className={op.source === 'ia' ? 'gamme__ia' : undefined}>
              {/* ── TOUT EN DUR SAUF LE TEMPS ──
                  Cinq champs de saisie par ligne, soit trente sur une
                  gamme de six opérations : le tableau était un
                  formulaire, alors qu'on le lit vingt fois pour le
                  corriger une. Le numéro d'OP, la prise, le libellé, la
                  machine et la catégorie s'AFFICHENT ; seul le temps se
                  change, parce que c'est le seul chiffre sur lequel un
                  deviseur a un avis en le lisant.
                  Le reste n'est pas verrouillé pour autant : « Modifier »
                  rouvre la ligne entière. Sans ça, une opération ajoutée
                  à la main naîtrait sans nom ni machine — et la base
                  refuserait la ligne, ou pire, l'accepterait. */}
              <td className="num gamme__op">
                {op.op_num}
                {op.prise_num ? <span className="gamme__prise">P{op.prise_num}</span> : null}
              </td>
              <td>
                {ouvert ? (
                  <TextField hideLabel label={`Libellé de l’opération ${op.op_num}`}
                    value={op.libelle ?? ''} disabled={disabled}
                    onChange={(e) => modifier(i, { libelle: e.target.value })} />
                ) : (
                  <span className="gamme__libelle">{titreDeLOperation(op)}</span>
                )}
                {/* ── Le POURQUOI de l'opération, en puces ──
                    C'était deux pavés de texte gris empilés sous le
                    libellé : le « détail » (ce qu'on fait) et la
                    « justification » (pourquoi ce temps). Personne ne
                    lit ça, donc personne ne le corrige, donc le
                    programme n'apprend rien de la seule chose qui vaut
                    règle. */}
                {/* ── ON N'EN CACHE PLUS AUCUNE — 26 août 2026 ──
                    Charles : « ça m'embête de devoir cliquer sur "une
                    raison de plus", pourquoi ça n'indique pas tout de
                    suite ? »

                    Il a raison, et le repli se retournait contre son
                    propre but. Il existait pour éviter le pavé illisible
                    — mais réglé à UNE puce, il cachait presque tout, sur
                    l'écran même où l'on relit la gamme pour la corriger.
                    Une explication qu'il faut déplier n'est pas lue,
                    donc pas contestée, donc le programme n'apprend rien.

                    Les puces restent : c'est elles qui rendent le texte
                    lisible. C'est la TRONCATURE qui part. */}
                <Puces texte={op.detail} max={Infinity} />
                <Puces texte={op.justification} max={Infinity} />
                {/* ── La machine et la catégorie SOUS le libellé ──
                    Elles avaient chacune leur colonne : deux colonnes
                    pour deux mots, dans un tableau qui doit tenir dans
                    une demi-largeur. Elles restent LISIBLES — la
                    catégorie et son unité disent si le temps se
                    multiplie par la quantité, et c'est l'argument le
                    plus fort du devis en série — mais sur la même ligne
                    que ce qu'elles qualifient. */}
                {!ouvert ? (
                  <p className="gamme__meta">
                    {machine?.name ?? 'sans machine'}
                    <span className="gamme__sep">·</span>
                    {LIBELLE_CATEGORIE[op.categorie] ?? op.categorie}
                    <span className="gamme__unite">{LIBELLE_UNITE[op.unite] ?? op.unite}</span>
                  </p>
                ) : (
                  <div className="gamme__edition">
                    <Select hideLabel label={`Machine de l’opération ${op.op_num}`}
                      value={op.machine_id ? String(op.machine_id) : ''} disabled={disabled}
                      options={[
                        // La programmation n'a pas de machine : elle se
                        // fait devant un poste de CAO.
                        { value: '', label: 'Sans machine' },
                        ...machines.map((m) => ({ value: String(m.id), label: m.name })),
                      ]}
                      onChange={(e) => modifier(i, {
                        machine_id: e.target.value ? Number(e.target.value) : null,
                      })} />
                    <Select hideLabel label={`Catégorie de l’opération ${op.op_num}`}
                      value={op.categorie} disabled={disabled}
                      options={CATEGORIES_GAMME.map((c) => ({
                        value: c, label: LIBELLE_CATEGORIE[c],
                      }))}
                      onChange={(e) => changerCategorie(i, e.target.value)} />
                    {/* L'unité suit la CATÉGORIE : un montage ne peut pas
                        être « par pièce », la base le refuse et le menu
                        ne doit donc pas l'offrir. */}
                    <Select hideLabel label={`Unité de l’opération ${op.op_num}`}
                      value={op.unite} disabled={disabled}
                      options={(UNITES_PAR_CATEGORIE[op.categorie] ?? []).map((u) => ({
                        value: u, label: LIBELLE_UNITE[u],
                      }))}
                      onChange={(e) => modifier(i, { unite: e.target.value })} />
                  </div>
                )}
              </td>
              <td className="num">
                {/* ── LE TEMPS DE BASE DE L'IA, TOUJOURS À CÔTÉ DU CADRE ──
                    Réunion du 1er septembre, redemandé par Charles le
                    15 : la proposition d'origine reste affichée, en
                    clair, contre le champ — MÊME quand elle est égale au
                    temps retenu. Ne l'afficher qu'en cas d'écart laissait
                    une ligne muette qu'on ne distinguait pas d'une ligne
                    jamais proposée.

                    C'est la PREMIÈRE proposition, et une révision ne la
                    réécrit pas (`gammeHeritage.js`). Absente (`null`) —
                    une opération ajoutée à la main — on n'écrit rien
                    plutôt qu'un « IA 0 » qui serait faux. */}
                <div className="gamme__temps">
                  <NumberField hideLabel label={`Temps de l’opération ${op.op_num}, en minutes`}
                    value={op.temps_min} min={0} disabled={disabled}
                    onChange={(v) => modifier(i, { temps_min: v })} />
                  {op.temps_min_ia != null ? (
                    <span className="gamme__propose" title="Temps de base proposé par l’IA">
                      IA {formatMesure(op.temps_min_ia, 'min')}
                    </span>
                  ) : null}
                </div>
              </td>
              {/* ── DEUX ICÔNES, et une étiquette « proposé » en moins ──
                  « Modifier », « Retirer » et une pastille « PROPOSÉ »
                  faisaient trois mots par ligne, dans un tableau qui
                  doit tenir dans une demi-largeur. Les deux gestes
                  deviennent deux icônes titrées ; la provenance devient
                  un LISERÉ de couleur sur le bord gauche de la ligne
                  (`gamme__ia`), lisible d'un coup d'œil sur toute la
                  gamme au lieu d'être répétée six fois. */}
              <td className="gamme__actions">
                {!disabled ? (
                  <>
                    {/* ── L'ORDRE SE CORRIGE À L'ÉCRAN ──
                        Une opération ajoutée arrive en fin de gamme,
                        après le contrôle. Deux flèches suffisent — et
                        elles échangent les NUMÉROS d'OP, pas seulement
                        les places : le serveur relit `ORDER BY op_num`,
                        donc un déplacement qui ne toucherait qu'à
                        l'affichage disparaîtrait au rechargement. */}
                    <span className="gamme__ordre">
                      <Button size="sm" variant="ghost" icon
                        title="Monter cette opération"
                        aria-label={`Monter l’opération ${op.op_num}`}
                        disabled={!peutMonter}
                        onClick={() => deplacer(i, -1)}>
                        ↑
                      </Button>
                      <Button size="sm" variant="ghost" icon
                        title="Descendre cette opération"
                        aria-label={`Descendre l’opération ${op.op_num}`}
                        disabled={!peutDescendre}
                        onClick={() => deplacer(i, 1)}>
                        ↓
                      </Button>
                    </span>
                    <Button size="sm" variant="ghost" icon
                      title={ouvert ? 'Terminer la modification' : 'Modifier cette ligne'}
                      aria-label={ouvert ? `Terminer la modification de l’opération ${op.op_num}` : `Modifier l’opération ${op.op_num}`}
                      onClick={() => onOuvrir(ouvert ? null : i)}>
                      {ouvert ? '✓' : '✎'}
                    </Button>
                    <Button size="sm" variant="ghost" icon
                      title="Retirer cette opération"
                      aria-label={`Retirer l’opération ${op.op_num}`}
                      onClick={() => onRetirer(i)}>
                      ✕
                    </Button>
                  </>
                ) : null}
              </td>
            </tr>
  );
}

export default LigneGamme;
